/**
 * pdfConverter.js — local PDF → JPEG conversion utility
 *
 * Uses pdfjs-dist (bundled via npm, no CDN required).
 * Renders all PDF pages onto a single merged JPEG canvas at 1.5× scale,
 * which is fast enough for preview/upload and keeps file sizes reasonable.
 *
 * Usage:
 *   import { convertPdfToJpeg } from '../utils/pdfConverter';
 *   const jpegFile = await convertPdfToJpeg(pdfFile, { onProgress });
 */

// pdfjs-dist (upgraded 3.11.174 -> 6.2.108 for a CVE fix) now calls the very
// recent Promise.withResolvers() internally and ships no polyfill for it, so
// conversion threw silently on any browser/device without it (caught below
// as "Could not process file"). pdfjs-dist does ship a `legacy/` build with
// polyfills for exactly this, but that build is itself webpack-bundled and
// its `__webpack_module_cache__` collides with CRA's own webpack runtime at
// minify time — so polyfill the one missing method directly instead.
if (typeof Promise.withResolvers !== 'function') {
  Promise.withResolvers = function withResolvers() {
    let resolve, reject;
    const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
    return { promise, resolve, reject };
  };
}

import * as pdfjsLib from 'pdfjs-dist';

// Served as a plain static file from public/pdfjs/ (copied at build/start time by
// scripts/copy-pdf-worker.js) rather than referenced via `new URL(..., import.meta.url)`.
// CRA5's webpack build runs that pattern's target through babel-loader, which corrupts
// this prebuilt file by injecting @babel/runtime imports it can't resolve into the
// bundle graph, leaking an absolute build-machine filesystem path into the output and
// breaking the module in every browser ("Failed to fetch dynamically imported module").
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';

/**
 * @param {File}     file          — a File object of type application/pdf
 * @param {object}   [options]
 * @param {number}   [options.scale=1.5]          — render scale (1.5 = good quality + speed)
 * @param {number}   [options.jpegQuality=0.85]   — JPEG compression 0–1
 * @param {number}   [options.pageGap=8]           — px gap between pages in merged output
 * @param {Function} [options.onProgress]          — cb(pagesDone, totalPages)
 * @returns {Promise<File>} — JPEG File object with same base name as input
 */
export async function convertPdfToJpeg(file, options = {}) {
  const {
    scale = 1.5,
    jpegQuality = 0.85,
    pageGap = 8,
    onProgress = null,
  } = options;

  const arrayBuffer = await file.arrayBuffer();

  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;
  const numPages = pdf.numPages;

  // Render each page to its own off-screen canvas
  const pageCanvases = [];
  for (let i = 1; i <= numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width  = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);

    await page.render({
      canvasContext: canvas.getContext('2d'),
      viewport,
    }).promise;

    pageCanvases.push(canvas);
    if (onProgress) onProgress(i, numPages);
  }

  // Merge all page canvases into a single vertical strip
  const maxWidth    = Math.max(...pageCanvases.map(c => c.width));
  const totalHeight =
    pageCanvases.reduce((sum, c) => sum + c.height, 0) +
    pageGap * (numPages - 1);

  const merged = document.createElement('canvas');
  merged.width  = maxWidth;
  merged.height = totalHeight;

  const ctx = merged.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, maxWidth, totalHeight);

  let y = 0;
  for (const c of pageCanvases) {
    ctx.drawImage(c, 0, y);
    y += c.height + pageGap;
  }

  // Export to JPEG File
  return new Promise((resolve, reject) => {
    merged.toBlob(
      (blob) => {
        if (!blob) { reject(new Error('Canvas toBlob failed')); return; }
        const outName = file.name.replace(/\.pdf$/i, '.jpg');
        resolve(new File([blob], outName, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      jpegQuality
    );
  });
}
